import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import AddressFormField from '../fields/AddressFormField';
import NavRow from '../fields/NavRow';
import TextField from '../fields/TextField';
import { TaskDefinition, TaskStepRenderArgs } from '../QuestionnaireShell';
import { FirmValues } from '../types';
import { zodResolver } from '../validation';

const URL_PATTERN = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.){1,}([a-zA-Z]{2,})(\/[^\s]*)?$/;

const schema = z.object({
  firmName: z
    .string({ required_error: 'Firm Name is required but is missing' })
    .min(1, 'Firm Name is required but is missing'),
  websiteUrl: z.string().regex(URL_PATTERN, 'Must be a valid website URL').or(z.literal('')).optional(),
  addressForm: z.object({
    address1: z.string().min(1, 'Address is required'),
    address2: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zipCode: z.string().min(1, 'ZIP code is required'),
  }),
});

const DEFAULTS: FirmValues = {
  firmName: '',
  websiteUrl: '',
  addressForm: { address1: '', address2: '', city: '', state: '', zipCode: '' },
};

const FirmInfoForm = ({ initialValues, onComplete, onBack, onCancelEdit, submitLabel }: TaskStepRenderArgs) => {
  const defaults = (initialValues as FirmValues | undefined) ?? DEFAULTS;
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<FirmValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit((values) => {
    // Drop websiteUrl when empty — the server schema runs its URL pattern on
    // the field whenever it's present, and "" fails the pattern even though
    // the field isn't required.
    const cleaned: FirmValues = {
      firmName: values.firmName,
      addressForm: values.addressForm,
      ...(values.websiteUrl ? { websiteUrl: values.websiteUrl } : {}),
    };
    onComplete(cleaned);
  });

  return (
    <form className="rnq-form" onSubmit={(e) => e.preventDefault()}>
      <TextField control={control} name="firmName" label="Firm Name" required />
      <TextField control={control} name="websiteUrl" label="Website URL" type="url" />
      <AddressFormField control={control} setValue={setValue} namePrefix="addressForm" required />
      <NavRow
        onBack={onBack}
        onCancelEdit={onCancelEdit}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={!isValid}
      />
    </form>
  );
};

const FirmInfo: TaskDefinition = {
  key: 'task3',
  label: 'Firm',
  title: 'Law Firm Information',
  description: 'Please provide information about your law firm.',
  render: (args) => <FirmInfoForm {...args} />,
};

export default FirmInfo;
